import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, FormControl, FormLabel, Paper, Stack, TextField, Typography, useTheme } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import PersonAddIcon from '@mui/icons-material/PersonAdd';

import Avatar from "components/uiElements/avatar/Avatar";
import LanguageAutocomplete, { LanguageOption } from "components/uiElements/inputs/LanguageAutocomplete";
import TimezoneAutocomplete from "components/uiElements/inputs/TimezoneAutocomplete";
import PasswordConfirmation from "components/profilePage/PasswordConfirmation";
import { getInitials } from "system/InfegyUser";
import APIRequest, { APIError } from "system/APIRequest";

interface InvitationSender {
  avatar: string;
  email: string;
  name: string;
}

interface Invitation {
  account: string;
  account_id: string;
  created_at: string;
  email: string;
  invite_id: string;
  invited_by: InvitationSender;
}

interface NewUserPreferences {
  avatar?: string;
  locale?: string;
  time_zone?: string;
}

interface NewUser {
  name: string,
  password: string;
  preferences?: NewUserPreferences;
}

enum RegistrationState {
  LookupInvitation,
  Requirements,
  Preferences,
  Complete
}

export default function UserRegistrationPage(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { i18n } = useTranslation();

  const [state, setState] = useState(RegistrationState.LookupInvitation);
  const [invitation, setInvitation] = useState<Invitation | null>(null);

  // LookupInvitation state data
  const [isLoadingInvitation, setIsLoadingInvitation] = useState(false);
  const [hasInvitationError, setHasInvitationError] = useState(false);
  const [invitationError, setInvitationError] = useState<string>("");
  const [invitationAlreadyComplete, setInvitationAlreadyComplete] = useState<boolean>(false);
  const [hasBlockedError, setHasBlockedError] = useState<boolean>(false);
  const [hasServerError, setHasServerError] = useState<boolean>(false);

  // CreateUser state data
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [hasCreateUserError, setHasCreateUserError] = useState(false);
  const [createUserError, setCreateUserError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  // SetupPreferences state data
  const defaultLanguageLocale = useRef<string>("");
  const defaultTimeZone = useRef<string>("")
  const [avatar, setAvatar] = useState("");
  const [languageLocale, setLanguageLocale] = useState(defaultLanguageLocale.current);
  const [timeZone, setTimeZone] = useState(defaultTimeZone.current);

  const queryParams = new URLSearchParams(location.search);
  const inviteId = queryParams.get("invite");
  const inviteeEmail = queryParams.get("email");

  async function getInvitation(): Promise<Invitation | null> {
    const queryParams = {
      invite: inviteId ?? "",
      email: inviteeEmail ?? ""
    };

    let response;
    try {
      response = await APIRequest("invite", queryParams, undefined, false, 3, [409]);
    } catch (e: any) {
      const error = e as APIError;
      if (error.code === 401 || error.code === 403) {
        setHasBlockedError(true);
        return null;
      } else if (error.code === 409) {
        setInvitationAlreadyComplete(true);
        return null;
      } else if (error.code >= 500) {
        setHasServerError(true);
      } else {
        // If we aren't handling this exception, allow it to bubble on up.
        throw e;
      }
    }
    return response;
  }

  function handleBackToRequirements(): void {
    setIsPasswordValid(false);
    setState(RegistrationState.Requirements)
  }

  async function createUser(): Promise<any> {
    const queryParams = {
      invite: inviteId ?? "",
      email: inviteeEmail ?? ""
    };
    let newUser: NewUser = {
      name,
      password
    };
    let preferences: NewUserPreferences = {};
    if (avatar) {
      preferences.avatar = avatar;
    }
    if (languageLocale && languageLocale != defaultLanguageLocale.current) {
      preferences.locale = languageLocale;
    }
    if (timeZone && timeZone != defaultTimeZone.current) {
      preferences.time_zone = timeZone;
    }
    if (Object.keys(preferences).length) {
      newUser.preferences = preferences;
    }
    return await APIRequest("invite", queryParams, {
      body: JSON.stringify(newUser),
      method: "POST"
    });
  }

  function handleNameChange(event: ChangeEvent<HTMLInputElement>): void {
    setName(event.target.value);
  }

  function handlePasswordChange(isValid: boolean, password: string): void {
    setPassword(password);
    setIsPasswordValid(isValid);
  }

  function handleAvatarChange(image: string): void {
    setAvatar(image);
  }

  function handleLanguageDefault(language: string): void {
    defaultLanguageLocale.current = language;
  }

  function handleLanguageChange(language: LanguageOption): void {
    setLanguageLocale(language.locale);
  }

  function handleTimeZoneDefault(timeZone: string): void {
    defaultTimeZone.current = timeZone;
  }

  function handleTimeZoneChange(timeZone: string): void {
    setTimeZone(timeZone);
  }

  async function handleSaveUser(): Promise<void> {
    try {
      setHasCreateUserError(false);
      setCreateUserError("");
      setIsCreatingUser(true);
      const response = await createUser();
      setState(RegistrationState.Complete);
    } catch (error: any) {
      setHasCreateUserError(true);
      setInvitationError(error?.message);
    } finally {
      setIsCreatingUser(false);
    }
  }

  function handleGoToLogin(): void {
    navigate("/login");
  }

  useEffect(() => {
    if (state === RegistrationState.LookupInvitation) {
      const doValidateInvitation = async () => {
        try {
          setIsLoadingInvitation(true);
          const response = await getInvitation();
          if (response) {
            setInvitation(response);
            setState(RegistrationState.Requirements);
          }
        } catch (error: any) {
          setHasInvitationError(true);
          if(error?.code === 409) {
            setInvitationError(error?.server_message);
          } else {
            setInvitationError(error?.message);
          }
        } finally {
          setIsLoadingInvitation(false);
        }
      };
      doValidateInvitation();
    }
  }, [state])

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        justifyContent: "center",
        alignContent: "center"
      }}>
      <Stack
        direction="column"
        spacing={2}
        sx={{
          padding: 2,
          marginTop: 3,
          width: 700,
          maxWidth: 700
        }}>
        <Paper sx={{ paddingTop: 2, paddingRight: 6, paddingBottom: 2, paddingLeft: 6, width: "100%"}}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}>
            <img style={{ height: "4rem", width: "auto", margin: "0 auto", display: "block" }} src="/img/Infegy_Logo_FullColor.svg" alt="Infegy Starscape logo" />
            <Typography variant="h2">
              {i18n.t("user-registration-page.application-title")}
            </Typography>
            <Typography variant="subtitle1">
            {i18n.t("user-registration-page.page-title")}
            </Typography>
          </Box>
          <Box sx={{
            marginTop: 2
          }}>
            {/* Form content area */}
            {state === RegistrationState.LookupInvitation &&
              <Stack
                spacing={2}
                sx={{
                  display: "flex",
                  alignItems: "center"
                }}>
                {isLoadingInvitation &&
                  <>
                  <CircularProgress />
                  <Box>{i18n.t("user-registration-page.invitation.loading")}</Box>
                  </>
                }
                {hasBlockedError &&
                  <Stack sx={{gap: 2}}>
                    <Typography>{i18n.t("user-registration-page.errors.blocked.info")}</Typography>
                    <Typography>{i18n.t("user-registration-page.errors.blocked.instructions")}</Typography>
                    <Button variant="contained" onClick={() => {
                      window.location.reload();
                    }}>
                      {i18n.t("user-registration-page.errors.reload-page-button")}
                    </Button>
                  </Stack>
                }
                {hasServerError &&
                  <Stack sx={{gap: 2}}>
                    <Typography>{i18n.t("api-request.server-error-message")}</Typography>
                    <Button variant="contained" onClick={() => {
                      window.location.reload();
                    }}>
                      {i18n.t("user-registration-page.errors.reload-page-button")}
                    </Button>
                  </Stack>
                }
                {invitationAlreadyComplete &&
                  <Stack sx={{
                    gap: 2
                  }}>
                    <Typography>{i18n.t("user-registration-page.invitation.already-complete.description")}</Typography>
                    <Typography>{i18n.t("user-registration-page.invitation.already-complete.next-steps")}</Typography>
                    <Button variant="contained" onClick={() => navigate("/login")}>
                    {i18n.t("user-registration-page.invitation.already-complete.login-button")}
                    </Button>
                  </Stack>
                }
              </Stack>
            }
            {state === RegistrationState.Requirements &&
            <>
              <Stack spacing={1} direction="row">
                {invitation?.invited_by &&
                  <Avatar image={invitation?.invited_by?.avatar} initials={getInitials(invitation?.invited_by?.name ?? "")} size={64} />
                }
                <Stack spacing={1} sx={{
                  justifyContent: "center"
                }}>
                  {invitation?.invited_by &&
                    <Typography><b>{invitation?.invited_by.name}</b> {i18n.t("user-registration-page.requirements.invitation")}</Typography>
                  }
                  <Typography>{i18n.t("user-registration-page.requirements.instructions")}</Typography>
                </Stack>
              </Stack>
              <Stack spacing={2} sx={{
                marginTop: 3
              }}>
                <FormControl>
                  <FormLabel sx={{fontSize: "1rem"}}>{i18n.t("user-registration-page.requirements.name")}</FormLabel>
                  <TextField autoComplete="off" value={name} onChange={handleNameChange} size="small"></TextField>
                </FormControl>
                <PasswordConfirmation passwordLabel={i18n.t("login.form.password")} onPasswordChange={handlePasswordChange} />
              </Stack>
              </>
            }
            {state === RegistrationState.Preferences &&
              <Stack spacing={1}>
                <Box>
                  {i18n.t("user-registration-page.preferences.flavor-text")}
                </Box>
                <FormControl>
                <FormLabel sx={{fontSize: "1rem"}}>{i18n.t("user-registration-page.preferences.avatar")}</FormLabel>
                  <Avatar size={64} isEditable={true} image={avatar} onChange={handleAvatarChange} />
                </FormControl>
                <FormControl>
                  <FormLabel sx={{fontSize: "1rem"}}>{i18n.t("user-registration-page.preferences.language")}</FormLabel>
                  <LanguageAutocomplete size="small" locale={languageLocale} onDefault={handleLanguageDefault} onChange={handleLanguageChange} />
                </FormControl>
                <FormControl>
                  <FormLabel sx={{fontSize: "1rem"}}>{i18n.t("user-registration-page.preferences.time-zone")}</FormLabel>
                  <TimezoneAutocomplete size="small" timeZone={timeZone} onDefault={handleTimeZoneDefault} onChange={handleTimeZoneChange} />
                </FormControl>
              </Stack>
            }
            {state === RegistrationState.Complete &&
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center"
                }}>
                <Stack spacing={2}>
                  <Typography variant="body1">{i18n.t("user-registration-page.account-created.success")}</Typography>
                  <Stack spacing={1} direction="row">
                    <Avatar image={avatar} initials={getInitials(name)} onChange={handleAvatarChange} />
                    <Typography variant="h3">{name}</Typography>
                  </Stack>
                  <Typography>{i18n.t("user-registration-page.account-created.begin")}</Typography>
                  <Stack direction="row" sx={{
                    justifyContent: "center"
                  }}>
                    <Button variant="contained" color="primary" onClick={handleGoToLogin}>{i18n.t("user-registration-page.account-created.go-to-login")}</Button>
                  </Stack>
                </Stack>
              </Box>
            }
          </Box>
          {(hasCreateUserError || hasInvitationError) &&
            <Box sx={{
              marginTop: 1
            }}>
              {/* Error box */}
              {state === RegistrationState.LookupInvitation && hasInvitationError && !invitationAlreadyComplete &&
                <Stack spacing={1}>
                  <Box>{i18n.t("user-registration-page.errors.invitation.info")}</Box>
                  <Box>{i18n.t("user-registration-page.errors.invitation.instructions")}</Box>
                  <Alert variant="filled" severity="error">{invitationError}</Alert>
                </Stack>
              }
              {state !== RegistrationState.LookupInvitation && !invitationAlreadyComplete &&
                <Stack spacing={1} sx={{
                  marginTop: 3
                }}>
                  <Box>{i18n.t("user-registration-page.errors.user-creation.info")}</Box>
                  <Alert variant="filled" severity="error">{createUserError}</Alert>
                </Stack>
              }
            </Box>
          }
          <Box>
            {/* Form button bar */}
            {state === RegistrationState.Requirements &&
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "end",
                  marginTop: 2
                }}>
                <Button disabled={!name || name.length < 3 || !isPasswordValid} variant="contained" onClick={() => setState(RegistrationState.Preferences)}>{i18n.t("continue")}</Button>
              </Box>
            }
            {state === RegistrationState.Preferences &&
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  marginTop: 2,
                  justifyContent: "space-between"
                }}>
                <Button variant="contained" color="secondary" onClick={handleBackToRequirements}>
                  {i18n.t("user-registration-page.preferences.button-back")}
                </Button>
                <Button
                  color="success"
                  loading={isCreatingUser}
                  loadingPosition="start"
                  onClick={handleSaveUser}
                  startIcon={<PersonAddIcon />}
                  variant="contained" >
                  {i18n.t("user-registration-page.preferences.button-create-user")}
                </Button>
              </Stack>
            }
          </Box>
        </Paper>
        <Stack direction="row" spacing={1} sx={{
          justifyContent: "center"
        }}>
          <Typography variant="subtitle1">{i18n.t("user-registration-page.existing-user")}</Typography>
          <Link to="/login">{i18n.t("user-registration-page.log-in")}</Link>
        </Stack>
      </Stack>
    </Box>
  );
}
